import React from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, FileText } from 'lucide-react';

const Privacy = () => {
  const lastUpdated = 'January 15, 2025';

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-teal-50 to-green-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Shield className="w-16 h-16 text-teal-600 mx-auto mb-6" />
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Legal & Privacy
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Your privacy and trust are important to us
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Privacy Policy */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-16"
            >
              <div className="flex items-center mb-6">
                <FileText className="w-8 h-8 text-teal-600 mr-3" />
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                  Privacy Policy
                </h2>
              </div>
              <p className="text-sm text-gray-500 mb-8">Last updated: {lastUpdated}</p>

              <div className="prose prose-lg max-w-none text-gray-600 space-y-6">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">1. Information We Collect</h3>
                  <p className="leading-relaxed">
                    We collect information that you provide directly to us, including when you create an account, book a service, or contact us. This may include your name, email address, phone number, address, and payment information.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">2. How We Use Your Information</h3>
                  <p className="leading-relaxed mb-2">
                    We use the information we collect to:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Provide, maintain, and improve our services</li>
                    <li>Process and complete transactions</li>
                    <li>Send you technical notices and support messages</li>
                    <li>Respond to your comments and questions</li>
                    <li>Communicate with you about services, offers, and events</li>
                    <li>Monitor and analyze trends and usage</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">3. Information Sharing</h3>
                  <p className="leading-relaxed">
                    We do not sell your personal information. We may share your information with service professionals to facilitate bookings, with service providers who assist in our operations, and when required by law or to protect our rights.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">4. Data Security</h3>
                  <p className="leading-relaxed">
                    We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">5. Your Rights</h3>
                  <p className="leading-relaxed mb-2">
                    You have the right to:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Access and receive a copy of your personal information</li>
                    <li>Correct inaccurate or incomplete information</li>
                    <li>Request deletion of your personal information</li>
                    <li>Object to or restrict certain processing</li>
                    <li>Withdraw consent where we rely on it</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">6. Cookies and Tracking</h3>
                  <p className="leading-relaxed">
                    We use cookies and similar tracking technologies to collect information about your browsing activities. You can control cookies through your browser settings.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Terms of Service */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-16"
            >
              <div className="flex items-center mb-6">
                <FileText className="w-8 h-8 text-teal-600 mr-3" />
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                  Terms of Service
                </h2>
              </div>
              <p className="text-sm text-gray-500 mb-8">Last updated: {lastUpdated}</p>

              <div className="prose prose-lg max-w-none text-gray-600 space-y-6">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">1. Acceptance of Terms</h3>
                  <p className="leading-relaxed">
                    By accessing and using Maidly's services, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">2. Service Description</h3>
                  <p className="leading-relaxed">
                    Maidly is a platform that connects customers with independent service professionals. We are not the employer of service professionals and do not provide home services directly.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">3. User Accounts</h3>
                  <p className="leading-relaxed">
                    You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account. You must provide accurate information and keep it updated.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">4. Booking and Payments</h3>
                  <p className="leading-relaxed">
                    When you book a service, you agree to pay the fees displayed at the time of booking. Payments are processed securely through our payment partners. Cancellation policies apply as stated at booking.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">5. User Conduct</h3>
                  <p className="leading-relaxed mb-2">
                    You agree not to:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Violate any laws or regulations</li>
                    <li>Infringe on intellectual property rights</li>
                    <li>Harass or harm service professionals or other users</li>
                    <li>Provide false or misleading information</li>
                    <li>Interfere with the platform's operation</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">6. Limitation of Liability</h3>
                  <p className="leading-relaxed">
                    Maidly is not liable for any damages arising from the use of our services or services provided by professionals. We make no warranties about service quality, although we strive to maintain high standards.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">7. Dispute Resolution</h3>
                  <p className="leading-relaxed">
                    Any disputes arising from these terms will be resolved through arbitration in Mumbai, Maharashtra, in accordance with Indian law.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Refund Policy */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center mb-6">
                <FileText className="w-8 h-8 text-teal-600 mr-3" />
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                  Refund Policy
                </h2>
              </div>
              <p className="text-sm text-gray-500 mb-8">Last updated: {lastUpdated}</p>

              <div className="prose prose-lg max-w-none text-gray-600 space-y-6">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">1. 100% Satisfaction Guarantee</h3>
                  <p className="leading-relaxed">
                    We stand behind the quality of our services. If you're not satisfied with a completed service, contact us within 24 hours and we'll work to make it right.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">2. Cancellation Policy</h3>
                  <p className="leading-relaxed">
                    Cancellations made more than 2 hours before the scheduled service time receive a full refund. Cancellations within 2 hours may incur a cancellation fee. No-shows are non-refundable.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">3. Refund Process</h3>
                  <p className="leading-relaxed">
                    Approved refunds are processed within 5-7 business days to your original payment method. If you paid via cash, refunds will be processed to your registered bank account.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Contact Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mt-16 bg-gradient-to-br from-teal-50 to-green-50 rounded-3xl p-8 text-center"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Have Questions?
              </h3>
              <p className="text-gray-600 mb-6">
                If you have any questions about our policies, please contact us
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="mailto:legal@maidly.in" className="text-teal-600 font-semibold hover:underline">
                  legal@maidly.in
                </a>
                <span className="hidden sm:inline text-gray-400">|</span>
                <a href="tel:18001234567" className="text-teal-600 font-semibold hover:underline">
                  1800-123-4567
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Privacy;
